/**
 * MarketModule - Módulo Educativo do Mercado da Borracha
 * 
 * INTEGRADO À PLATAFORMA - Acesso via Dashboard
 * Controle de acesso: Planos pagos + Trial para gratuitos
 * 
 * Baseado no tripé: Justiça, Segurança, Simplicidade
 * Fontes: CNA, IEA/SAA-SP, APABOR, CONAB
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import SupplyChain from './SupplyChain';
import WhatIsTBS from './WhatIsTBS';
import PriceFormation from './PriceFormation';
import Glossary from './Glossary';
import OfficialSources from './OfficialSources';
import QuotationsPanel from './QuotationsPanel';
import TrendPanel from './TrendPanel';
import ProfitCalculator from './ProfitCalculator';
import UserGuide from './UserGuide';
import MarketAnalysis from './MarketAnalysis';
import DataStatusDashboard from './DataStatusDashboard';
import PriceFactors from './PriceFactors';
import { useTranslation } from '../../translations';

const MarketModule = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('quotations');
  const [showGuide, setShowGuide] = useState(false);
  const [hasSeenGuide, setHasSeenGuide] = useState(false);
  const [quotations, setQuotations] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);
  
  // Trial status (reutiliza a mesma API do Calendário)
  const [trialStatus, setTrialStatus] = useState(null);
  const [trialLoading, setTrialLoading] = useState(true);

  // Carregar status do trial
  const loadTrialStatus = useCallback(async () => {
    try {
      setTrialLoading(true);
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${API_URL}/api/calendar-trial-status`);
      if (response.ok) {
        const data = await response.json();
        setTrialStatus(data);
      } else {
        // Se não conseguir carregar, permite acesso (fallback seguro)
        setTrialStatus({ allowed: true, days_remaining: -1 });
      }
    } catch (error) {
      console.error('Error loading trial status:', error);
      setTrialStatus({ allowed: true, days_remaining: -1 });
    } finally {
      setTrialLoading(false);
    }
  }, []);

  // Buscar cotações
  const fetchQuotations = useCallback(async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${API_URL}/api/market/quotations`);
      if (response.ok) {
        const data = await response.json();
        const quotationsMap = {};
        data.quotations.forEach(q => {
          quotationsMap[q.type] = q;
        });
        setQuotations(quotationsMap);
      }
    } catch (error) {
      console.error('Error fetching quotations:', error);
    }
  }, []);

  // Função para forçar refresh
  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
    fetchQuotations();
  };

  // Carregar trial status e cotações
  useEffect(() => {
    loadTrialStatus();
    fetchQuotations();
  }, [loadTrialStatus, fetchQuotations]);

  // Verificar se o usuário já viu o guia
  useEffect(() => {
    const seen = localStorage.getItem('heveatalent_market_guide_seen');
    if (!seen) {
      // Mostrar guia automaticamente na primeira visita
      setTimeout(() => setShowGuide(true), 1000);
    } else {
      setHasSeenGuide(true);
    }
  }, []);

  const handleCloseGuide = () => {
    setShowGuide(false);
    setHasSeenGuide(true);
    localStorage.setItem('heveatalent_market_guide_seen', 'true');
  };

  const tabs = [
    { id: 'quotations', label: `💰 ${t('quotations')}`, icon: '💰' },
    { id: 'status', label: `📡 ${t('dataStatus')}`, icon: '📡' },
    { id: 'analysis', label: `📊 ${t('marketAnalysis')}`, icon: '📊' },
    { id: 'calculator', label: `🧮 ${t('calculator')}`, icon: '🧮' },
    { id: 'factors', label: '📈 Fatores de Preço', icon: '📈' },
    { id: 'chain', label: `🔗 ${t('priceChain') || 'Cadeia'}`, icon: '🔗' },
    { id: 'tbs', label: '📐 TBS/DRC', icon: '📐' },
    { id: 'glossary', label: `📖 ${t('glossary')}`, icon: '📖' },
    { id: 'sources', label: `🌐 ${t('sources')}`, icon: '🌐' },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'quotations':
        return <TrendPanel key={refreshKey} />;
      case 'quotations_old':
        return <QuotationsPanel key={refreshKey} />;
      case 'status':
        return <DataStatusDashboard quotations={quotations} onRefresh={handleRefresh} />;
      case 'analysis':
        return <MarketAnalysis key={refreshKey} />;
      case 'calculator':
        return <ProfitCalculator />;
      case 'factors':
        return <PriceFactors />;
      case 'chain':
        return <SupplyChain />;
      case 'tbs':
        return <WhatIsTBS />;
      case 'glossary':
        return <Glossary />;
      case 'sources':
        return <OfficialSources />;
      default:
        return <QuotationsPanel />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white p-4 md:p-8">
      {/* Trial Status Banners */}
      {!trialLoading && trialStatus && (
        <div className="max-w-5xl mx-auto mb-4">
          {/* Trial Expired - Block Access */}
          {trialStatus.trial_expired && (
            <div className="bg-red-50 border border-red-300 rounded-lg p-6 text-center">
              <div className="text-4xl mb-4">⏰</div>
              <h3 className="text-xl font-bold text-red-800 mb-2">{t('trialExpiredTitle') || 'Período de teste encerrado'}</h3>
              <p className="text-red-700 mb-4">
                {t('trialExpiredMarketMsg') || 'Seu período de teste para o módulo de Mercado expirou.'}
                <br />
                <span className="text-sm">{t('upgradeToAccess') || 'Assine um plano para continuar acessando.'}</span>
              </p>
              <div className="flex justify-center gap-4">
                <Button 
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => window.location.hash = 'planos'}
                >
                  {t('viewPlansFrom') || 'Ver planos a partir de'} R$ {trialStatus.upgrade_price?.toFixed(2) || '29.90'}/{t('month') || 'mês'}
                </Button>
              </div>
            </div>
          )}

          {/* CRITICAL Alert - 1 day remaining */}
          {trialStatus.alert_level === 'critical' && !trialStatus.trial_expired && (
            <div className="bg-red-100 border-2 border-red-500 rounded-lg p-4 flex items-center justify-between animate-pulse">
              <div className="flex items-center">
                <span className="text-3xl mr-3">🚨</span>
                <div>
                  <p className="font-bold text-red-800 text-lg">
                    {t('lastDayOfTrial') || 'Último dia do teste!'}
                  </p>
                  <p className="text-sm text-red-700">
                    {t('lastDayTrialMarketMsg') || 'Amanhã você perderá acesso ao módulo de Mercado.'}
                  </p>
                </div>
              </div>
              <Button 
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={() => window.location.hash = 'planos'}
              >
                {t('upgradeNow') || 'Assinar agora'}
              </Button>
            </div>
          )}

          {/* URGENT Alert - 7 days or less */}
          {trialStatus.alert_level === 'urgent' && !trialStatus.trial_expired && (
            <div className="bg-orange-100 border-2 border-orange-500 rounded-lg p-4 flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-2xl mr-3">⚠️</span>
                <div>
                  <p className="font-bold text-orange-800">
                    {t('trialEndingSoon') || 'Teste acabando!'} ({trialStatus.days_remaining} {t('daysRemaining') || 'dias restantes'})
                  </p>
                  <p className="text-sm text-orange-700">
                    {t('upgradeToKeepAccess') || 'Assine para manter o acesso ao módulo de Mercado.'}
                  </p>
                </div>
              </div>
              <Button 
                className="bg-orange-600 hover:bg-orange-700 text-white"
                onClick={() => window.location.hash = 'planos'}
              >
                {t('plans') || 'Planos'}
              </Button>
            </div>
          )}

          {/* WARNING Alert - 15 days or less */}
          {trialStatus.alert_level === 'warning' && !trialStatus.trial_expired && (
            <div className="bg-amber-50 border border-amber-400 rounded-lg p-4 flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-2xl mr-3">⏳</span>
                <div>
                  <p className="font-semibold text-amber-800">
                    {t('trialExpiresIn') || 'Teste expira em'} {trialStatus.days_remaining} {t('days') || 'dias'}
                  </p>
                  <p className="text-sm text-amber-700">
                    {t('seeUpgradeOptions') || 'Veja as opções de assinatura.'}
                  </p>
                </div>
              </div>
              <Button 
                variant="outline" 
                className="border-amber-500 text-amber-700 hover:bg-amber-100"
                onClick={() => window.location.hash = 'planos'}
              >
                {t('plans') || 'Planos'}
              </Button>
            </div>
          )}

          {/* INFO Alert - 30 days or less */}
          {trialStatus.alert_level === 'info' && !trialStatus.trial_expired && (
            <div className="bg-blue-50 border border-blue-300 rounded-lg p-3 flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-xl mr-2">ℹ️</span>
                <p className="text-sm text-blue-700">
                  <strong>{t('trialPeriod') || 'Período de teste'}:</strong> {trialStatus.days_remaining} {t('daysRemaining') || 'dias restantes'}
                  {trialStatus.trial_days_total && ` (${t('of') || 'de'} ${trialStatus.trial_days_total} ${t('days') || 'dias'})`}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Se trial expirou, não mostra o conteúdo */}
      {trialStatus?.trial_expired ? null : (
        <>
          {/* Guia do Usuário */}
          {showGuide && <UserGuide onClose={handleCloseGuide} />}

          {/* Header */}
          <div className="max-w-5xl mx-auto mb-8">
            <div className="text-center mb-6">
              <h1 className="text-3xl md:text-4xl font-bold text-green-800 mb-2">
                📊 {t('understandRubberMarket') || 'Entenda o Mercado da Borracha'}
              </h1>
              <p className="text-gray-600 text-lg">
                {t('marketInfoForDecision') || 'Informações de mercado para tomada de decisão.'}
              </p>
              
              {/* Botão do Guia */}
          <button
            onClick={() => setShowGuide(true)}
            className="mt-4 inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-full hover:bg-green-700 transition-colors shadow-lg"
          >
            <span className="text-xl">📚</span>
            <span className="font-medium">{t('viewCompleteGuide') || 'Ver Guia Completo'}</span>
            {!hasSeenGuide && (
              <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full animate-pulse">
                {t('new') || 'NOVO'}
              </span>
            )}
          </button>
        </div>

        {/* Tripé Card */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-green-100 border border-green-300 rounded-lg p-4 text-center">
            <span className="text-3xl">⚖️</span>
            <h3 className="font-bold text-green-800 mt-2">{t('fairness').toUpperCase()}</h3>
            <p className="text-sm text-green-700">{t('dataVerifiableOfficial')}</p>
          </div>
          <div className="bg-blue-100 border border-blue-300 rounded-lg p-4 text-center">
            <span className="text-3xl">🔒</span>
            <h3 className="font-bold text-blue-800 mt-2">{t('security').toUpperCase()}</h3>
            <p className="text-sm text-blue-700">{t('yourDataPrivateProtected') || 'Seus dados são privados e protegidos'}</p>
          </div>
          <div className="bg-purple-100 border border-purple-300 rounded-lg p-4 text-center">
            <span className="text-3xl">✨</span>
            <h3 className="font-bold text-purple-800 mt-2">{t('simplicity').toUpperCase()}</h3>
            <p className="text-sm text-purple-700">{t('easyToUnderstandUse')}</p>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-2">
            <span className="text-xl">⚠️</span>
            <div>
              <p className="text-amber-800 font-medium">{t('importantWarning') || 'Aviso Importante'}</p>
              <p className="text-amber-700 text-sm">
                {t('educationalContentDisclaimer') || 'Este conteúdo é educativo, baseado em fontes oficiais (IEA, APABOR, CNA, CONAB). Os preços e metodologias apresentados são referências - consulte sempre sua cooperativa ou usina para negociações específicas.'}
              </p>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex flex-wrap gap-2 justify-center mb-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-green-600 text-white shadow-lg'
                  : 'bg-white text-gray-600 hover:bg-green-50 border border-gray-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto">
        {renderContent()}
      </div>

      {/* Botão flutuante de ajuda */}
      <button
        onClick={() => setShowGuide(true)}
        className="fixed bottom-6 right-6 w-14 h-14 bg-green-600 text-white rounded-full shadow-xl hover:bg-green-700 transition-colors flex items-center justify-center text-2xl z-40"
        title={t('viewUserGuide') || 'Ver Guia de Uso'}
      >
        ❓
      </button>

      {/* Footer */}
      <div className="max-w-5xl mx-auto mt-8 text-center text-gray-500 text-sm">
        <p>{t('developedBasedOn')}</p>
        <p className="mt-1">{t('lastUpdate')}: {new Date().toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}</p>
      </div>
        </>
      )}
    </div>
  );
};

export default MarketModule;
